import React, { useState } from 'react';
import { X, Eye, EyeOff, Upload, FileText, ShieldCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { verificationAPI, authAPI } from '../utils/api';
import { useSimpleAuthEnabled } from '../hooks/useSimpleAuthEnabled';

interface ExtractedFields {
  name?: string;
  licenseType?: string;
  licenseNumber?: string;
  documentNumber?: string;
  issueDate?: string;
  specialty?: string;
}

interface SignupModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToLogin: () => void;
}

const SignupModal: React.FC<SignupModalProps> = ({ isOpen, onClose, onSwitchToLogin }) => {
  const [step, setStep] = useState(1); // 1: 기본정보, 2: 면허증 업로드
  const simpleAuthEnabled = useSimpleAuthEnabled();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    hospital: '',
    specialization: '',
  });
  const [doctorLicenseFile, setDoctorLicenseFile] = useState<File | null>(null);
  const [specialistLicenseFile, setSpecialistLicenseFile] = useState<File | null>(null);
  const [doctorLicenseUrl, setDoctorLicenseUrl] = useState<string>('');
  const [specialistLicenseUrl, setSpecialistLicenseUrl] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [userId, setUserId] = useState<number | null>(null);
  const [doctorExtracted, setDoctorExtracted] = useState<ExtractedFields | null>(null);
  const [specialistExtracted, setSpecialistExtracted] = useState<ExtractedFields | null>(null);

  // 간편인증(PASS)
  const [passBirth, setPassBirth] = useState('');
  const [passPhone, setPassPhone] = useState('');
  const [passProvider, setPassProvider] = useState('pass');
  const [passTelecom, setPassTelecom] = useState('');
  const [passRrn7, setPassRrn7] = useState('');
  const [passConsent, setPassConsent] = useState(false);
  const [passLoading, setPassLoading] = useState(false);
  const [passMessage, setPassMessage] = useState('');

  const { signup } = useAuth();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'doctor' | 'specialist') => {
    const file = e.target.files?.[0];
    if (file) {
      if (type === 'doctor') {
        setDoctorLicenseFile(file);
      } else {
        setSpecialistLicenseFile(file);
      }
    }
  };

  // 자동검증(auto_verified) 직후 stale JWT 갱신 → 최신 사용자/배지 반영을 위해 토큰 리프레시 후 새로고침
  const handleAutoVerified = async () => {
    try {
      const rt = localStorage.getItem('refreshToken');
      if (rt) {
        const res = await authAPI.refreshToken(rt);
        if (res.data?.token) localStorage.setItem('token', res.data.token);
      }
      const prof = await authAPI.getProfile();
      const freshUser = prof.data?.user || prof.data;
      if (freshUser) localStorage.setItem('user', JSON.stringify(freshUser));
    } catch {
      /* 토큰 갱신 실패해도 다음 로그인 시 반영됨 */
    }
    window.location.reload();
  };

  const handleStep1Submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('비밀번호가 일치하지 않습니다.');
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError('비밀번호는 최소 6자 이상이어야 합니다.');
      setLoading(false);
      return;
    }

    try {
      const response = await signup({
        email: formData.email,
        password: formData.password,
        name: formData.name,
        hospital: formData.hospital || undefined,
        specialization: formData.specialization || undefined,
      });
      
      // userId 저장하고 2단계로 이동
      if (response?.userId) {
        setUserId(response.userId);
      }
      setStep(2);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resetAndClose = () => {
    onClose();
    setStep(1);
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      name: '',
      hospital: '',
      specialization: '',
    });
    setDoctorLicenseFile(null);
    setSpecialistLicenseFile(null);
    setDoctorLicenseUrl('');
    setSpecialistLicenseUrl('');
    setDoctorExtracted(null);
    setSpecialistExtracted(null);
    setPassBirth('');
    setPassPhone('');
    setPassMessage('');
  };

  const handleStep2Submit = async () => {
    setUploading(true);
    setError('');

    try {
      // 업로드 + OCR 추출 + pending 라우팅을 한 번에 처리 (서버에서 주민번호 마스킹)
      if (doctorLicenseFile) {
        const res = await verificationAPI.ocr(doctorLicenseFile, 'doctor');
        setDoctorLicenseUrl(res.data.imageUrl || '');
        setDoctorExtracted(res.data.extracted || null);
      }

      if (specialistLicenseFile) {
        const res = await verificationAPI.ocr(specialistLicenseFile, 'specialist');
        setSpecialistLicenseUrl(res.data.imageUrl || '');
        setSpecialistExtracted(res.data.extracted || null);
      }

      // 추출 결과가 있으면 사용자에게 보여주기 위해 모달 유지, 없으면 닫기
      if (doctorLicenseFile || specialistLicenseFile) {
        // 추출 결과 패널은 렌더에서 표시됨. 파일 선택만 초기화.
        setDoctorLicenseFile(null);
        setSpecialistLicenseFile(null);
      } else {
        resetAndClose();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || '면허증 업로드 중 오류가 발생했습니다.');
    } finally {
      setUploading(false);
    }
  };

  const handlePassVerify = async () => {
    setError('');
    setPassMessage('');
    if (!formData.name) {
      setError('이름이 필요합니다.');
      return;
    }
    if (!/^\d{8}$/.test(passBirth)) {
      setError('생년월일은 YYYYMMDD 8자리로 입력해주세요.');
      return;
    }
    if (!/^\d{7}$/.test(passRrn7)) {
      setError('주민등록번호 뒤 7자리를 입력해주세요.');
      return;
    }
    if (!passConsent) {
      setError('본인확인 및 면허정보 조회에 동의해주세요.');
      return;
    }
    setPassLoading(true);
    try {
      const init = await verificationAPI.passInit({
        provider: passProvider,
        name: formData.name,
        birthDate: passBirth,
        phone: passPhone || undefined,
        telecom: passProvider === 'pass' && passTelecom ? passTelecom : undefined,
        rrn7: passRrn7,
      });
      // 주민번호 뒤 7자리는 전송 후 메모리에서 즉시 폐기 (재요청 시 재입력).
      setPassRrn7('');
      setPassMessage('PASS 앱에서 본인인증을 승인한 뒤 잠시만 기다려주세요...');

      const complete = await verificationAPI.passComplete(init.data.txId);
      const { verificationStatus, score, requiresRelogin } = complete.data;

      if (verificationStatus === 'verified') {
        setPassMessage(`✓ 자동 검증 완료 (신뢰도 ${score}/100). 인증 정보를 갱신합니다...`);
        if (requiresRelogin) {
          await handleAutoVerified();
          return;
        }
      } else if (verificationStatus === 'pending') {
        setPassMessage(`검증 신청이 접수되었습니다 (신뢰도 ${score}/100). 관리자 검토 후 인증 배지가 부여됩니다.`);
      } else {
        setPassMessage('검증이 반려되었습니다. 면허 정보를 확인 후 다시 시도해주세요.');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || '간편인증에 실패했습니다.');
    } finally {
      setPassLoading(false);
    }
  };

  const handleSkipLicenses = () => {
    // 면허증 업로드 건너뛰기
    resetAndClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">회원가입</h2>
            <p className="text-sm text-gray-500 mt-1">
              {step === 1 ? '1단계: 기본 정보' : '2단계: 면허증 업로드 (선택)'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {step === 1 ? (
          <form onSubmit={handleStep1Submit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              이메일 *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400"
              autoComplete="email"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              비밀번호 *
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 pr-10"
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              비밀번호 확인 *
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400 pr-10"
                autoComplete="new-password"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              이름 *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              소속 병원
            </label>
            <input
              type="text"
              name="hospital"
              value={formData.hospital}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              전문 분야
            </label>
            <input
              type="text"
              name="specialization"
              value={formData.specialization}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-400"
            />
          </div>

          {error && (
            <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gray-900 text-white py-2 px-4 rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? '처리 중...' : '다음 단계'}
          </button>
        </form>
        ) : (
          <div className="space-y-4">
            {/* 면허증 업로드 안내 */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h3 className="font-semibold text-gray-900 mb-2">면허증 자동 인식 및 검증</h3>
              <p className="text-sm text-gray-600 mb-2">
                의사면허증 또는 전문의자격증을 업로드하시면 <strong>AI가 자동으로 문서번호와 면허번호를 추출</strong>합니다.
              </p>
              <ul className="text-xs text-gray-600 space-y-1 mb-2">
                <li>✓ 자동 OCR 인식으로 정보 추출</li>
                <li>✓ 관리자 승인 후 인증 배지 부여</li>
                <li>✓ 발급일 90일 이내 문서 진위확인 가능</li>
              </ul>
              {/* 처리방침과 일치: 면허증 이미지는 외부 AI(Google Gemini)로 판독하고 검증 결정 즉시 삭제한다 */}
              <p className="text-xs text-gray-700 bg-white border border-gray-200 rounded-md px-3 py-2 mb-2">
                주민등록번호 뒷자리는 가리고 올려 주세요. 면허증 이미지는 외부 AI(Google)로 판독하며, 검증이 끝나면 바로 삭제합니다. 
                <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="underline">개인정보 처리방침</a>
              </p>
              <p className="text-xs text-gray-600">
                📄 발급처: <a 
                  href="https://lic.mohw.go.kr" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="underline hover:text-gray-900"
                >
                  보건복지부 면허민원
                </a> | 
                <a 
                  href="https://lic.mohw.go.kr/instt/docuNo_check.do?MENU_ID=D-01-01" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="underline hover:text-gray-900 ml-2"
                >
                  진위확인
                </a>
              </p>
            </div>

            {/* 간편인증은 실제 면허조회 수단이 연결된 경우에만 — 아니면 주민번호를 받지 않는다 */}
            {simpleAuthEnabled && (
            <>
            {/* 간편인증(PASS) 즉시 검증 */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h3 className="font-semibold text-purple-900 mb-2 flex items-center">
                <ShieldCheck className="w-5 h-5 mr-2" />
                간편인증으로 즉시 검증 (권장)
              </h3>
              <p className="text-sm text-purple-800 mb-3">
                간편인증(PASS/카카오/네이버 등)으로 보건복지부 면허정보와 자동 대조합니다. 대부분 즉시 인증됩니다.
              </p>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <select
                  value={passProvider}
                  onChange={(e) => setPassProvider(e.target.value)}
                  className="px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm bg-white"
                >
                  <option value="pass">통신사 PASS</option>
                  <option value="kakao">카카오톡</option>
                  <option value="naver">네이버</option>
                  <option value="toss">토스</option>
                  <option value="kb">KB모바일</option>
                  <option value="samsung">삼성패스</option>
                  <option value="shinhan">신한</option>
                  <option value="hana">하나</option>
                  <option value="nh">NH</option>
                </select>
                {passProvider === 'pass' ? (
                  <select
                    value={passTelecom}
                    onChange={(e) => setPassTelecom(e.target.value)}
                    className="px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm bg-white"
                  >
                    <option value="">통신사 선택</option>
                    <option value="0">SKT</option>
                    <option value="1">KT</option>
                    <option value="2">LG U+</option>
                    <option value="3">SKT 알뜰폰</option>
                    <option value="4">KT 알뜰폰</option>
                    <option value="5">LG U+ 알뜰폰</option>
                  </select>
                ) : (
                  <div />
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <input
                  type="text"
                  inputMode="numeric"
                  value={passBirth}
                  onChange={(e) => setPassBirth(e.target.value.replace(/\D/g, '').slice(0, 8))}
                  placeholder="생년월일 (YYYYMMDD)"
                  className="px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                />
                <input
                  type="tel"
                  value={passPhone}
                  onChange={(e) => setPassPhone(e.target.value.replace(/\D/g, '').slice(0, 11))}
                  placeholder="휴대폰번호 (선택)"
                  className="px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm"
                />
              </div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm text-purple-900 font-medium whitespace-nowrap">주민번호</span>
                <input
                  type="text"
                  inputMode="numeric"
                  value={passBirth.slice(0, 6)}
                  readOnly
                  tabIndex={-1}
                  placeholder="앞 6자리"
                  className="w-24 px-3 py-2 border border-purple-200 bg-purple-50 text-purple-400 rounded-md text-sm text-center"
                />
                <span className="text-purple-400">-</span>
                <input
                  type="password"
                  inputMode="numeric"
                  autoComplete="off"
                  value={passRrn7}
                  onChange={(e) => setPassRrn7(e.target.value.replace(/\D/g, '').slice(0, 7))}
                  placeholder="뒤 7자리"
                  className="flex-1 px-3 py-2 border border-purple-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 text-sm tracking-widest"
                />
              </div>
              <p className="text-[11px] text-purple-600 mb-2">
                주민등록번호는 본인확인을 위해 암호화 전송되며 서버에 저장하지 않습니다.
              </p>
              <label className="flex items-start gap-2 mb-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={passConsent}
                  onChange={(e) => setPassConsent(e.target.checked)}
                  className="mt-0.5"
                />
                <span className="text-xs text-purple-800">
                  본인확인 및 보건복지부 의료인 면허정보 조회에 동의합니다.
                </span>
              </label>
              <button
                type="button"
                onClick={handlePassVerify}
                disabled={passLoading}
                className="w-full bg-purple-600 text-white py-2 px-4 rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
              >
                {passLoading ? 'PASS 인증 진행 중...' : '간편인증으로 검증하기'}
              </button>
              {passMessage && (
                <p className="text-xs text-purple-800 mt-2 whitespace-pre-wrap">{passMessage}</p>
              )}
            </div>

            <div className="flex items-center my-2">
              <div className="flex-1 border-t border-gray-200" />
              <span className="px-3 text-xs text-gray-400">또는 면허증 이미지로 신청</span>
              <div className="flex-1 border-t border-gray-200" />
            </div>

            </>
            )}

            {/* 의사면허증 업로드 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FileText className="inline w-4 h-4 mr-1" />
                의사면허증
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'doctor')}
                  className="hidden"
                  id="doctor-license"
                />
                <label
                  htmlFor="doctor-license"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  {doctorLicenseFile ? (
                    <span className="text-sm text-green-600 font-medium">
                      ✓ {doctorLicenseFile.name}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-500">
                      클릭하여 파일 선택
                    </span>
                  )}
                </label>
              </div>
            </div>

            {/* 전문의자격증 업로드 */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FileText className="inline w-4 h-4 mr-1" />
                전문의자격증 (선택)
              </label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'specialist')}
                  className="hidden"
                  id="specialist-license"
                />
                <label
                  htmlFor="specialist-license"
                  className="cursor-pointer flex flex-col items-center"
                >
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  {specialistLicenseFile ? (
                    <span className="text-sm text-green-600 font-medium">
                      ✓ {specialistLicenseFile.name}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-500">
                      클릭하여 파일 선택
                    </span>
                  )}
                </label>
              </div>
            </div>

            {/* OCR 추출 결과 */}
            {(doctorExtracted || specialistExtracted) && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                <h4 className="font-semibold text-green-900 text-sm">📄 추출된 면허 정보</h4>
                {doctorExtracted && (
                  <div className="text-sm text-green-800">
                    <p className="font-medium">의사면허증</p>
                    {doctorExtracted.licenseNumber && <p>면허번호: {doctorExtracted.licenseNumber}</p>}
                    {doctorExtracted.documentNumber && <p>문서확인번호: {doctorExtracted.documentNumber}</p>}
                    {doctorExtracted.issueDate && <p>발급일: {doctorExtracted.issueDate}</p>}
                    {!doctorExtracted.licenseNumber && !doctorExtracted.documentNumber && (
                      <p className="text-green-700">자동 추출이 어려워 관리자가 직접 확인합니다.</p>
                    )}
                  </div>
                )}
                {specialistExtracted && (
                  <div className="text-sm text-green-800">
                    <p className="font-medium">전문의자격증</p>
                    {specialistExtracted.licenseNumber && <p>자격번호: {specialistExtracted.licenseNumber}</p>}
                    {specialistExtracted.documentNumber && <p>문서확인번호: {specialistExtracted.documentNumber}</p>}
                    {specialistExtracted.specialty && <p>전문과목: {specialistExtracted.specialty}</p>}
                  </div>
                )}
                <p className="text-xs text-green-700">추출된 정보는 관리자 승인 후 인증 배지로 반영됩니다.</p>
              </div>
            )}

            {error && (
              <div className="text-red-600 text-sm bg-red-50 p-3 rounded-md">
                {error}
              </div>
            )}

            {/* 버튼들 */}
            <div className="flex space-x-3">
              <button
                onClick={handleSkipLicenses}
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300"
              >
                {doctorExtracted || specialistExtracted ? '닫기' : '나중에 업로드'}
              </button>
              <button
                onClick={handleStep2Submit}
                disabled={uploading || (!doctorLicenseFile && !specialistLicenseFile)}
                className="flex-1 bg-gray-900 text-white py-2 px-4 rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {uploading ? '업로드 중...' : '면허증으로 신청'}
              </button>
            </div>
          </div>
        )}

        {step === 1 && (
          <p className="mt-4 text-xs text-gray-500 text-center leading-relaxed">
            가입하면 <a href="/privacy.html" target="_blank" rel="noopener noreferrer" className="underline">개인정보 처리방침</a>에 따라
            개인정보가 처리됩니다. 만 14세 미만은 가입할 수 없습니다.
          </p>
        )}
        {step === 1 && (
          <div className="mt-4 text-center">
            <span className="text-gray-600">이미 계정이 있으신가요? </span>
            <button
              onClick={onSwitchToLogin}
              className="text-gray-900 hover:underline font-medium"
            >
              로그인
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignupModal;
